import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import GuestLoginPrompt from "@/components/GuestLoginPrompt";
import { ArrowLeft, Plus, Minus, Lightning, User } from "@phosphor-icons/react";

export default function ProductPage() {
  const { slug, productId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { add, decrement, getStoreCart, pincode, subtotal, itemCount } = useCart();
  const [p, setP] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    api.get(`/storefront/${slug}`).then((r) => setStore(r.data));
    api.get(`/storefront/${slug}/product/${productId}`).then((r) => setP(r.data));
  }, [slug, productId]);

  if (!p || !store) return <div className="p-8">Loading…</div>;
  const cart = getStoreCart(slug);
  const inCart = cart.items.find((i) => i.product_id === p.id);
  const inv = Number(p.inventory ?? 0);
  const oos = inv <= 0;
  const lowStock = !oos && inv <= 5;
  const atMax = inCart && inCart.qty >= inv;

  const safeAdd = () => {
    if (oos) { toast.error(`'${p.name}' is out of stock`); return; }
    if ((inCart?.qty || 0) + 1 > inv) { toast.error(`Only ${inv} quantity available`); return; }
    add(slug, store.id, p);
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      <header className="sticky top-0 glass z-40 border-b border-gray-100 px-4 py-3 flex items-center gap-2">
        <Link to={`/${slug}`} className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-testid="pdp-back">
          <ArrowLeft size={18} />
        </Link>
        <div className="text-sm font-bold truncate flex-1 min-w-0">{store.name}</div>
        {!user && (
          <Link to="/login"
            onClick={() => sessionStorage.setItem("qd_return_to", `/${slug}/product/${productId}`)}
            className="inline-flex items-center gap-1 bg-[#15803D] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#166534]"
            data-testid="pdp-login-cta">
            <User size={12} weight="bold" /> Login
          </Link>
        )}
      </header>

      <div className="max-w-2xl mx-auto p-4">
        <div className="aspect-square rounded-2xl overflow-hidden bg-gray-50 mb-4">
          {p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt={p.name} />}
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Lightning weight="fill" size={12} className="text-[#15803D]" /> Delivery in 20 min
          {oos && <span className="ml-auto text-red-600 font-bold uppercase tracking-widest text-[10px]" data-testid="pdp-oos-badge">Out of stock</span>}
          {!oos && lowStock && <span className="ml-auto text-amber-700 font-bold text-[10px]" data-testid="pdp-low-stock">Only {inv} left</span>}
        </div>
        <h1 className="font-heading font-black text-2xl tracking-tight" data-testid="pdp-name">{p.name}</h1>
        <div className="text-sm text-gray-500 mt-1">{p.weight}</div>

        <div className="flex items-center gap-3 mt-3">
          <span className="font-heading font-black text-3xl">₹{p.price}</span>
          {p.mrp > p.price && (
            <>
              <span className="text-base text-gray-400 line-through">₹{p.mrp}</span>
              <span className="text-xs px-2 py-1 rounded-full bg-[#15803D] text-white font-bold">{p.discount_pct}% OFF</span>
            </>
          )}
        </div>

        <div className="mt-5 qd-card p-4">
          <h3 className="font-heading font-bold text-lg mb-2">About this product</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{p.description}</p>
          {p.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {p.tags.map((t, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">#{t}</span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          {oos ? (
            <button disabled
              className="col-span-2 bg-gray-200 text-gray-500 font-bold py-3 rounded-xl cursor-not-allowed"
              data-testid="pdp-sold-out">
              Sold out
            </button>
          ) : (
            <>
              {inCart ? (
                <div className="col-span-1 flex items-center justify-between bg-gray-100 rounded-xl px-2 py-2">
                  <button onClick={() => decrement(slug, p.id)} className="w-8 h-8 rounded-lg bg-white" data-testid="pdp-dec"><Minus size={16} weight="bold" /></button>
                  <span className="font-heading font-black text-lg">{inCart.qty}</span>
                  <button onClick={safeAdd} disabled={atMax}
                    className={`w-8 h-8 rounded-lg bg-white ${atMax ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="pdp-inc"><Plus size={16} weight="bold" /></button>
                </div>
              ) : (
                <button onClick={safeAdd}
                  className="col-span-1 qd-btn-green inline-flex items-center justify-center gap-2"
                  data-testid="pdp-add-to-cart">
                  Add to cart
                </button>
              )}
              <button onClick={() => { if (!inCart) safeAdd(); nav(`/${slug}/cart`); }}
                className="col-span-1 qd-btn-primary"
                data-testid="pdp-buy-now">
                Buy now
              </button>
            </>
          )}
        </div>
      </div>

      {itemCount(slug) > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30 p-3">
          <button onClick={() => nav(`/${slug}/cart`)}
            className="w-full max-w-3xl mx-auto bg-[#15803D] text-white rounded-2xl p-4 flex items-center justify-between shadow-2xl">
            <div>{itemCount(slug)} item · ₹{subtotal(slug)}</div>
            <div className="font-heading font-black">Cart →</div>
          </button>
        </div>
      )}

      <GuestLoginPrompt returnTo={`/${slug}/product/${productId}`} alwaysShow />
    </div>
  );
}
